import { FC, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import type { BadgeTemplate } from "../types";
import api from "../services/api";

export const BADGE_STORAGE_KEY = 'qrflow_badge_templates';

const migrateSide = (side: any, w: number, h: number): BadgeTemplate['front'] => {
    const elements: BadgeTemplate['front']['elements'] = side.elements || [];
    if (side.backgroundImage) {
        elements.unshift({
            id: `migrated_bg_${Date.now()}`,
            type: 'image' as const,
            x: 0, y: 0, width: w, height: h,
            fontSize: 0, fontStyle: 'normal' as const, color: '#ffffff', align: 'center' as const,
            imageData: side.backgroundImage,
        });
    }
    return { bgColor: side.bgColor ?? '#1e293b', elements };
};

export const parseTemplateData = (templateData: string): { front: BadgeTemplate['front'], back: BadgeTemplate['back'] } => {
    try {
        return JSON.parse(templateData);
    } catch {
        return { front: { bgColor: '#1e293b', elements: [] }, back: { bgColor: '#1e293b', elements: [] } };
    }
};

export const loadTemplatesFromLocalStorage = (): BadgeTemplate[] => {
    try {
        const stored = localStorage.getItem(BADGE_STORAGE_KEY);
        if (!stored) return [];
        const raw: any[] = JSON.parse(stored);
        return raw.map(t => ({
            ...t,
            dpi: t.dpi ?? 300,
            front: migrateSide(t.front, t.width, t.height),
            back: migrateSide(t.back, t.width, t.height),
        }));
    } catch { return []; }
};

export const loadTemplates = async (): Promise<BadgeTemplate[]> => {
    try {
        const response = await api.get<any>('/api/badge-templates/getAll');
        const data = response.data?.data || [];
        return data.map((t: any) => ({
            ...t,
            front: parseTemplateData(t.templateData).front,
            back: parseTemplateData(t.templateData).back,
        }));
    } catch {
        return [];
    }
};

const GUIDE_STEPS = [
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
        ),
        title: '1. Crea un template',
        desc: 'Clicca su "Nuovo Template" per aprire il designer. Scegli la dimensione del badge (es. Lanyard Portrait, Credit Card) oppure inserisci larghezza e altezza personalizzate in pixel.',
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 12h8M12 8v8"/></svg>
        ),
        title: '2. Aggiungi gli elementi',
        desc: 'Dal pannello sinistro puoi trascinare sul canvas questi elementi: Nome Persona, QR Code, Numero Badge, Area Accesso, Città/Location, Testo Fisso, Box colorato, Immagine di sfondo. Ogni elemento si sposta trascinandolo e si ridimensiona dagli angoli.',
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
        ),
        title: '3. Personalizza ogni elemento',
        desc: 'Cliccando su un elemento compare il pannello destro con tutte le opzioni: posizione X/Y, larghezza, altezza, colore testo, dimensione font, grassetto/corsivo, allineamento. Per i box puoi scegliere colore di sfondo, bordo e arrotondamento angoli.',
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/></svg>
        ),
        title: '4. Fronte e retro',
        desc: 'Il badge ha due lati: Fronte e Retro. Puoi passare da uno all\'altro con le tab in alto nel designer. Ogni lato ha i propri elementi e il proprio colore di sfondo, e verrà stampato separatamente.',
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
        ),
        title: '5. Salva il template',
        desc: 'Premi "Salva" in alto a destra nel designer. Il template viene memorizzato e comparirà in questa lista. Puoi modificarlo in qualsiasi momento cliccando l\'icona matita.',
    },
    {
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        ),
        title: '6. Genera i badge',
        desc: 'Dalla card del template clicca "Genera Badge". Carica il file Excel o CSV con i dati dei partecipanti (nome, area, città…) e il sistema creerà automaticamente un file ZIP con tutti i badge già compilati, pronti per la stampa.',
    },
];

const ELEMENTS_INFO = [
    { color: 'bg-sky-500',     label: 'Nome Persona',    desc: 'Mostra il nome del partecipante preso dal file dati.' },
    { color: 'bg-emerald-500', label: 'QR Code',         desc: 'QR code univoco per ogni badge, usato per la verifica accessi.' },
    { color: 'bg-violet-500',  label: 'Numero Badge',    desc: 'Numero progressivo assegnato automaticamente.' },
    { color: 'bg-amber-500',   label: 'Area Accesso',    desc: 'Indica l\'area o il tipo di accesso del partecipante (es. VIP, Staff).' },
    { color: 'bg-orange-500',  label: 'Città/Location',  desc: 'Città o sede dell\'evento, personalizzabile per ogni persona.' },
    { color: 'bg-slate-500',   label: 'Testo Fisso',     desc: 'Testo statico uguale per tutti i badge (es. nome evento, anno).' },
    { color: 'bg-pink-500',    label: 'Box/Rettangolo',  desc: 'Blocco colorato decorativo, utile per bande di colore o separatori.' },
    { color: 'bg-teal-500',    label: 'Immagine',        desc: 'Logo, sfondo fotografico o qualsiasi immagine caricata da file.' },
];

const BadgeTemplatesPage: FC = () => {
    const [templates, setTemplates] = useState<BadgeTemplate[]>([]);
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [migrating, setMigrating] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const base = location.pathname.startsWith('/admin') ? '/admin' : '/client';

    const fetchTemplates = async () => {
        const data = await loadTemplates();
        setTemplates(data);
        setLoading(false);
    };

    const migrateFromLocalStorage = async () => {
        const localTemplates = loadTemplatesFromLocalStorage();
        if (localTemplates.length === 0) return;
        setMigrating(true);
        try {
            for (const t of localTemplates) {
                await api.post('/api/badge-templates/save', {
                    name: t.name,
                    width: t.width,
                    height: t.height,
                    dpi: t.dpi,
                    templateData: JSON.stringify({ front: t.front, back: t.back }),
                });
            }
            localStorage.removeItem('qrflow_badge_templates');
            fetchTemplates();
        } catch (err) {
            console.error('Migration failed:', err);
        } finally {
            setMigrating(false);
        }
    };

    useEffect(() => {
        fetchTemplates().then(() => {
            migrateFromLocalStorage();
        });
    }, []);

    const handleDelete = async (id: number) => {
        try {
            await api.delete(`/api/badge-templates/delete/${id}`);
            const updated = templates.filter(t => t.id !== id);
            setTemplates(updated);
            setDeleteConfirm(null);
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const IconBadge = (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <rect x="2" y="3" width="20" height="18" rx="2"/><line x1="8" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="16" y2="21"/>
        </svg>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-amber-500"/>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Template Badge
                    </h1>
                    <p className="text-slate-400 mt-1 text-base">Crea e gestisci i template per la stampa badge</p>
                </div>
                <button
                    onClick={() => navigate(`${base}/badge-designer`)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white font-semibold rounded-xl transition-all shadow-lg shadow-amber-500/20 flex-shrink-0"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Nuovo Template
                </button>
            </div>

            {/* Guide */}
            <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
                <button
                    onClick={() => setGuideOpen(o => !o)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-700/20 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center text-amber-400">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                        </div>
                        <span className="text-white font-semibold text-sm">Come funziona il sistema badge?</span>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"
                        className={`text-slate-400 transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}>
                        <path d="m6 9 6 6 6-6"/>
                    </svg>
                </button>

                {guideOpen && (
                    <div className="px-5 pb-6 border-t border-slate-700/50">
                        <p className="text-slate-400 text-sm mt-4 mb-6 leading-relaxed">
                            Con il sistema badge puoi progettare graficamente il layout di un badge (fronte e retro), salvarlo come template riutilizzabile e poi generare in automatico tutti i badge per un evento partendo da un file con i dati dei partecipanti. I badge vengono esportati come immagini pronte per la stampa.
                        </p>

                        {/* Steps */}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                            {GUIDE_STEPS.map((s, i) => (
                                <div key={i} className="bg-slate-900/50 border border-slate-700/40 rounded-xl p-4 flex gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 flex-shrink-0 mt-0.5">
                                        {s.icon}
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold text-sm mb-1">{s.title}</p>
                                        <p className="text-slate-400 text-xs leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Elements legend */}
                        <div>
                            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-widest mb-3">Elementi disponibili nel designer</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {ELEMENTS_INFO.map((el, i) => (
                                    <div key={i} className="flex items-start gap-2.5">
                                        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${el.color}`}/>
                                        <div>
                                            <span className="text-slate-200 text-xs font-medium">{el.label}</span>
                                            <span className="text-slate-500 text-xs"> — {el.desc}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-5 p-3 bg-amber-500/8 border border-amber-500/20 rounded-xl flex gap-2.5">
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" className="text-amber-400 flex-shrink-0 mt-0.5"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            <p className="text-amber-300/80 text-xs leading-relaxed">
                                <strong className="text-amber-300">Suggerimento:</strong> Prima di generare i badge assicurati che il file dati contenga le colonne giuste (es. <em>nome</em>, <em>area</em>, <em>citta</em>). La riga 1 deve essere l'intestazione. Formati supportati: <strong>.xlsx</strong> e <strong>.csv</strong>.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {migrating && (
                <div className="p-4 bg-blue-900/20 border border-blue-800/50 text-blue-300 rounded-xl text-sm flex items-center gap-2">
                    <div className="animate-spin w-4 h-4 border-2 border-blue-300/30 border-t-blue-300 rounded-full"/>
                    Migrazione template da storage locale...
                </div>
            )}

            {templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 bg-slate-800/40 border border-slate-700/50 rounded-2xl">
                    <div className="w-20 h-20 rounded-2xl bg-slate-700/50 flex items-center justify-center mb-4 text-slate-500">
                        {IconBadge}
                    </div>
                    <p className="text-slate-300 text-lg font-semibold">Nessun template creato</p>
                    <p className="text-slate-500 text-sm mt-1">Crea il tuo primo template badge per iniziare</p>
                    <button
                        onClick={() => navigate(`${base}/badge-designer`)}
                        className="mt-6 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-xl transition-colors"
                    >
                        Crea il primo template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map(template => (
                        <div
                            key={template.id}
                            className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden hover:border-slate-600 transition-all hover:shadow-xl group"
                        >
                            {/* Preview fronte */}
                            <div className="bg-slate-900/70 h-52 flex items-center justify-center p-4 gap-3">
                                {(() => { const img = template.front.elements.find(e => e.type === 'image')?.imageData; return img ? (
                                    <img src={img} alt="Fronte" className="h-full w-auto object-contain rounded shadow-xl"/>
                                ) : (
                                    <div className="h-full aspect-[63/100] bg-slate-700 rounded border border-slate-600 flex flex-col items-center justify-center gap-2">
                                        <div className="text-slate-500">{IconBadge}</div>
                                        <span className="text-slate-600 text-xs">Nessuna immagine</span>
                                    </div>
                                ); })()}
                                {(() => { const img = template.back.elements.find(e => e.type === 'image')?.imageData; return img ? (
                                    <img src={img} alt="Retro" className="h-full w-auto object-contain rounded shadow-xl opacity-60 group-hover:opacity-100 transition-opacity"/>
                                ) : (
                                    <div className="h-full aspect-[63/100] bg-slate-700/50 rounded border border-slate-600/50 flex flex-col items-center justify-center opacity-60">
                                        <span className="text-slate-600 text-xs">Retro</span>
                                    </div>
                                ); })()}
                            </div>

                            {/* Info */}
                            <div className="p-4 border-t border-slate-700/50">
                                <h3 className="text-white font-bold text-lg truncate">{template.name}</h3>
                                <p className="text-slate-500 text-xs mt-0.5">
                                    {template.width}×{template.height}px &nbsp;·&nbsp;
                                    {template.front.elements.length + template.back.elements.length} elementi &nbsp;·&nbsp;
                                    {new Date(template.createdAt).toLocaleDateString('it-IT')}
                                </p>

                                <div className="flex gap-2 mt-4">
                                    <button
                                        onClick={() => navigate(`${base}/badge-generate/${template.id}`)}
                                        className="flex-1 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white text-sm font-semibold rounded-lg transition-all shadow-md shadow-amber-500/20 flex items-center justify-center gap-1.5"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                        Genera Badge
                                    </button>
                                    <button
                                        onClick={() => navigate(`${base}/badge-designer/${template.id}`)}
                                        className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
                                        title="Modifica template"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirm(template.id)}
                                        className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-lg transition-colors"
                                        title="Elimina template"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Confirm delete modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-center p-4 animate-fade-in">
                    <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl p-6">
                        <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4 text-red-500">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Elimina Template</h3>
                        <p className="text-slate-400 mb-6">Sei sicuro? Il template verrà eliminato definitivamente.</p>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="px-4 py-2.5 rounded-xl text-slate-300 hover:bg-slate-700 transition-colors font-medium"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => handleDelete(deleteConfirm)}
                                className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BadgeTemplatesPage;
